import { memo, useMemo, useState } from 'react';

import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  Button,
  CollapseCard,
  GridItem,
  notify,
  PendingContent,
  SearchInputField,
  Spinner,
  TableV2,
} from 'plume-ui';
import { ColumnType } from 'plume-ui/dist/components/TableV2/types';
import { CollapseCardStyles } from 'plume-ui/dist/components/Card/CollapseCard';

import { DependencyContainer } from '../../../../DependencyContainer';
import { MixPanelEvents } from '../../../../mixPanelEvents';
import {
  activePartnerAtom,
  configurationInfoModalAtom,
  emailModalAtom,
} from '../../../../state';
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import { ConfigurationEmailResponse } from '../../../Monitor/types';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { EmptyEmailObject } from './fixtures';
import ActionCell from '../Webhook/renderers/tableCellAction';
import ErrorBlock from '../../../../components/ErrorBlock/ErrorBlock';
import useEmails from '../../hooks/useEmails';
import { invalidateFetchCache } from 'plume-web-utils';
import { UseFetchCacheKeys } from '../../../../cacheConfig';

type EmailsProps = {
  editAllowed: boolean;
};

type EmailsStyles = {};

const { outreachService } = new DependencyContainer();

const Emails: AdjustableComponent<EmailsProps, EmailsStyles> = ({
  editAllowed,
}) => {
  const setModal = useSetRecoilState(emailModalAtom);
  const setInfoModal = useSetRecoilState(configurationInfoModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();

  const [emailSearch, setEmailSearch] = useState<string>('');

  const {
    isLoading: loadingEmails,
    data: emailList,
    error: emailsLoadingError,
  } = useEmails();

  const getEmailById = (id: string) => emailList?.find((url) => url.id === id);

  const filteredEmails = useMemo(
    () =>
      emailList?.filter(
        (email) =>
          email.name.toLowerCase().includes(emailSearch.toLowerCase()) ||
          email.email.toLowerCase().includes(emailSearch.toLowerCase()),
      ),
    [emailList, emailSearch],
  );

  const handleAddNew = () => {
    setModal({
      isOpen: true,
      type: 'add',
      email: EmptyEmailObject,
      refetchEmails: () => invalidateFetchCache(UseFetchCacheKeys.Emails),
    });
  };

  const handleEdit = (id: string) => {
    setModal({
      isOpen: true,
      type: 'edit',
      email: getEmailById(id) || EmptyEmailObject,
      refetchEmails: () => invalidateFetchCache(UseFetchCacheKeys.Emails),
    });
  };

  const notifyDeleteError = () => {
    notify({
      title: t('configuration.notifications.error.email.delete.title'),
      body: t('configuration.notifications.error.email.delete.body'),
      type: 'error',
    });
  };

  const handleDelete = async (id: string) => {
    if (!activePartner?.id) return;
    await outreachService
      .deleteEmail(activePartner.id, id)
      .then(() => {
        invalidateFetchCache(UseFetchCacheKeys.Emails);
        notify({
          title: t('configuration.notifications.success.email.delete.title'),
          body: t('configuration.notifications.success.email.delete.body'),
          type: 'success',
        });
        trackEvent({
          eventName: MixPanelEvents.EMAIL_SUCCESS,
          additionalContent: {
            INTERACTION: 'DELETE',
            EMAIL_ID: id,
          },
        });
      })
      .catch((error) => {
        notifyDeleteError();
        trackEvent({
          eventName: MixPanelEvents.EMAIL_FAILURE,
          additionalContent: {
            INTERACTION: 'DELETE',
            EMAIL_ID: id,
            ERROR_MESSAGE: error?.response?.data?.message || 'no message',
          },
        });
      });
  };

  const columns: ColumnType<ConfigurationEmailResponse>[] = useMemo(
    () => [
      {
        key: 'name',
        title: t('configuration.email.name'),
        dataIndex: 'name',
      },
      {
        key: 'email',
        title: t('configuration.email.email'),
        dataIndex: 'email',
      },
      {
        title: t('configuration.webhooks.autonomousMonitors'),
        key: 'autoMonitors',
        render: (rowData) => (
          <div className="Configuration__collapseCard__webhook__table__rendered-cell">
            <div className="Configuration__collapseCard__webhook__table__rendered-cell__monitors">
              {rowData.autoMonitorDetails?.length || 0}
              {Boolean(rowData.autoMonitorDetails?.length) && (
                <Button
                  styleVariant="tertiary"
                  onClick={() => {
                    setInfoModal({
                      isOpen: true,
                      type: 'outreachList',
                      monitorType: 'autonomous',
                      data: {
                        outreachId: rowData.id,
                        monitorDetails: emailList?.find(
                          (item) => item.id === rowData.id,
                        )?.autoMonitorDetails,
                      },
                    });
                  }}
                >
                  {t('configuration.webhooks.more')}
                </Button>
              )}
            </div>
          </div>
        ),
      },
      {
        title: t('configuration.webhooks.myMonitors'),
        key: 'myMonitors',
        render: (rowData) => (
          <div className="Configuration__collapseCard__webhook__table__rendered-cell">
            <div className="Configuration__collapseCard__webhook__table__rendered-cell__monitors">
              {rowData.monitorDetails?.length}
              {Boolean(rowData.monitorDetails?.length) && (
                <Button
                  styleVariant="tertiary"
                  onClick={() => {
                    setInfoModal({
                      isOpen: true,
                      type: 'outreachList',
                      data: {
                        outreachId: rowData.id,
                        monitorDetails: emailList?.find(
                          (item) => item.id === rowData.id,
                        )?.monitorDetails,
                      },
                    });
                  }}
                >
                  {t('configuration.webhooks.more')}
                </Button>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'actions',
        title: t('configuration.webhooks.action'),
        render: (rowData) => (
          <ActionCell
            id={rowData.id}
            editAllowed={editAllowed}
            del={handleDelete}
            edit={handleEdit}
          />
        ),
      },
    ],
    [emailList, editAllowed, t],
  );

  return (
    <GridItem colSpan="12" tabletColSpan="12">
      <CollapseCard
        title={
          <div className="Configuration__collapseCard__title">
            {t('configuration.emails.title')}
          </div>
        }
        subtitle={i18n.t('configuration.emails.subtitle')}
        classes={(current: CollapseCardStyles) => ({
          ...current,
          root: `${current.root} Configuration__collapseCard Configuration__emailCard`,
          content: `${current.content} Configuration__collapseCard__content`,
        })}
      >
        {emailsLoadingError && <ErrorBlock height={500} />}
        {!emailsLoadingError && (
          <>
            <div className="Configuration__collapseCardHeader">
              <SearchInputField
                placeholder={t('configuration.emails.searchEmails')}
                rounded
                onChange={(ev) => setEmailSearch(ev.target.value || '')}
              />
              <Button
                styleVariant="primary"
                onClick={handleAddNew}
                disabled={!editAllowed}
              >
                {t('configuration.actions.addNew')}
              </Button>
            </div>
            <PendingContent
              data-testId="loader"
              loading={loadingEmails}
              loader={Spinner}
            >
              {filteredEmails && (
                <div className="Configuration__emails">
                  <TableV2
                    virtual
                    stickyHeader
                    columns={columns}
                    data={filteredEmails}
                    rowKey={(e) => e.id}
                  />
                </div>
              )}
            </PendingContent>
          </>
        )}
      </CollapseCard>
    </GridItem>
  );
};

export default memo(Emails);
